import React, { useEffect, useState } from "react";
import AdminPagination from "./AdminPagination";
import api, { BACKEND_URL, showAlert } from "../../util/api";
import "./RichNewsEditor.css";


/*
|--------------------------------------------------------------------------
| Rich News Content Editor
|--------------------------------------------------------------------------
| Self-contained editor: no extra package is required.
| Supports:
| - Bold, italic, underline, strike
| - Font family and font size
| - Text/background colour
| - Headings, paragraph, blockquote, code/preformatted text
| - Ordered/unordered lists
| - Alignment, indent/outdent
| - Links, images, videos and documents
| - Tables, horizontal rules
| - Undo/redo, remove formatting
| - Visual mode + HTML/source-code mode
| - Local media files are embedded as data URLs so they can be saved
|   together with the article without requiring a second API endpoint.
*/
const RichNewsEditor = ({ value, onChange }) => {
    const editorRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const [sourceMode, setSourceMode] = useState(false);
    const [htmlSource, setHtmlSource] = useState(value || "");
    const [mediaType, setMediaType] = useState("image");
    const [isFullscreen, setIsFullscreen] = useState(false);

    const syncFromEditor = () => {
        const html = editorRef.current?.innerHTML ?? "";
        setHtmlSource(html);
        onChange(html);
    };

    useEffect(() => {
        if (!sourceMode && editorRef.current && editorRef.current.innerHTML !== (value || "")) {
            editorRef.current.innerHTML = value || "";
        }
        if (!sourceMode) {
            setHtmlSource(value || "");
        }
    }, [value, sourceMode]);

    const focusEditor = () => {
        if (!sourceMode) {
            editorRef.current?.focus();
        }
    };

    const exec = (command, commandValue = null) => {
        focusEditor();
        document.execCommand(command, false, commandValue);
        syncFromEditor();
    };

    const ask = (message, fallback = "") => {
        const result = window.prompt(message, fallback);
        return result === null ? null : result.trim();
    };

    const addLink = () => {
        focusEditor();
        const url = ask("Enter the URL:", "https://");
        if (!url) return;

        const selection = window.getSelection();
        const selectedText = selection?.toString();

        if (selectedText) {
            exec("createLink", url);
        } else {
            const text = ask("Enter the link text:", url);
            if (!text) return;
            exec("insertHTML", `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`);
        }
    };

    const insertTable = () => {
        const rows = parseInt(ask("Number of rows:", "3"), 10);
        const cols = parseInt(ask("Number of columns:", "3"), 10);

        if (!rows || !cols || rows < 1 || cols < 1 || rows > 20 || cols > 20) {
            return;
        }

        let html = '<table style="width:100%;border-collapse:collapse;margin:16px 0;"><tbody>';

        for (let r = 0; r < rows; r += 1) {
            html += "<tr>";
            for (let c = 0; c < cols; c += 1) {
                html += `<td style="border:1px solid #cbd5e1;padding:8px;min-width:60px;">${r === 0 ? "Header" : "Cell"}</td>`;
            }
            html += "</tr>";
        }

        html += "</tbody></table><p><br></p>";
        exec("insertHTML", html);
    };

    const insertHtml = () => {
        const html = window.prompt(
            "Paste HTML to insert at the cursor:",
            "<p>Your HTML here...</p>"
        );

        if (html === null) return;
        exec("insertHTML", html);
    };

    const toggleSource = () => {
        if (sourceMode) {
            setSourceMode(false);
            setTimeout(() => {
                if (editorRef.current) {
                    editorRef.current.innerHTML = htmlSource;
                }
                onChange(htmlSource);
            }, 0);
        } else {
            const current = editorRef.current?.innerHTML ?? value ?? "";
            setHtmlSource(current);
            setSourceMode(true);
        }
    };

    const handleSourceChange = (e) => {
        const html = e.target.value;
        setHtmlSource(html);
        onChange(html);
    };

    const handleMediaButton = (type) => {
        setMediaType(type);
        fileInputRef.current?.click();
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";

        if (!file) return;

        const maxBytes = 50 * 1024 * 1024;

        if (file.size > maxBytes) {
            window.alert("Please select a file smaller than 50MB.");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const src = reader.result;
            const safeName = escapeHtml(file.name);

            if (mediaType === "image") {
                exec(
                    "insertHTML",
                    `<figure style="margin:20px 0;text-align:center;"><img src="${src}" alt="${safeName}" style="max-width:100%;height:auto;border-radius:8px;" /><figcaption style="font-size:13px;color:#64748b;margin-top:6px;">${safeName}</figcaption></figure><p><br></p>`
                );
            } else if (mediaType === "video") {
                exec(
                    "insertHTML",
                    `<figure style="margin:20px 0;"><video controls preload="metadata" style="display:block;width:100%;max-width:100%;border-radius:8px;"><source src="${src}" type="${file.type}" />Your browser does not support the video tag.</video><figcaption style="font-size:13px;color:#64748b;margin-top:6px;">${safeName}</figcaption></figure><p><br></p>`
                );
            } else {
                exec(
                    "insertHTML",
                    `<p style="margin:14px 0;"><a href="${src}" download="${safeName}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:8px;padding:10px 14px;border:1px solid #cbd5e1;border-radius:8px;text-decoration:none;">📄 ${safeName}</a></p><p><br></p>`
                );
            }
        };

        reader.onerror = () => {
            window.alert("Unable to read the selected file.");
        };

        reader.readAsDataURL(file);
    };

    const clearEditor = () => {
        if (!window.confirm("Clear all article content?")) return;
        if (sourceMode) {
            setHtmlSource("");
            onChange("");
        } else {
            if (editorRef.current) editorRef.current.innerHTML = "";
            onChange("");
            setHtmlSource("");
        }
    };

    const toolbarButton = (label, title, onClick, active = false) => (
        <button
            type="button"
            className={`rich-editor-tool ${active ? "active" : ""}`}
            title={title}
            aria-label={title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
        >
            {label}
        </button>
    );

    return (
        <div className={`rich-editor ${isFullscreen ? "rich-editor-fullscreen" : ""}`}>
            <div className="rich-editor-header">
                <div>
                    <strong>News Content Editor</strong>
                    <small>
                        Format text, add media, or switch to HTML to edit the code directly.
                    </small>
                </div>

                <div className="rich-editor-header-actions">
                    <button
                        type="button"
                        className={`rich-editor-mode ${sourceMode ? "active" : ""}`}
                        onClick={toggleSource}
                    >
                        {sourceMode ? "</> Visual Editor" : "</> HTML Code"}
                    </button>

                    <button
                        type="button"
                        className="rich-editor-mode"
                        onClick={() => setIsFullscreen((previous) => !previous)}
                    >
                        {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    </button>
                </div>
            </div>

            {!sourceMode && (
                <div className="rich-editor-toolbar">
                    <div className="rich-editor-toolbar-group">
                        <select
                            className="rich-editor-select"
                            title="Text style"
                            defaultValue=""
                            onChange={(e) => {
                                if (e.target.value) exec("formatBlock", e.target.value);
                                e.target.value = "";
                            }}
                        >
                            <option value="">Style</option>
                            <option value="P">Paragraph</option>
                            <option value="H1">Heading 1</option>
                            <option value="H2">Heading 2</option>
                            <option value="H3">Heading 3</option>
                            <option value="H4">Heading 4</option>
                            <option value="H5">Heading 5</option>
                            <option value="H6">Heading 6</option>
                            <option value="BLOCKQUOTE">Blockquote</option>
                            <option value="PRE">Preformatted</option>
                        </select>

                        <select
                            className="rich-editor-select"
                            title="Font family"
                            defaultValue=""
                            onChange={(e) => {
                                if (e.target.value) exec("fontName", e.target.value);
                                e.target.value = "";
                            }}
                        >
                            <option value="">Font</option>
                            <option value="Arial">Arial</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Tahoma">Tahoma</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Trebuchet MS">Trebuchet MS</option>
                            <option value="Verdana">Verdana</option>
                            <option value="Courier New">Courier New</option>
                        </select>

                        <select
                            className="rich-editor-select"
                            title="Font size"
                            defaultValue=""
                            onChange={(e) => {
                                if (e.target.value) exec("fontSize", e.target.value);
                                e.target.value = "";
                            }}
                        >
                            <option value="">Size</option>
                            <option value="1">Small</option>
                            <option value="2">Normal</option>
                            <option value="3">Medium</option>
                            <option value="4">Large</option>
                            <option value="5">X-Large</option>
                            <option value="6">XX-Large</option>
                            <option value="7">Huge</option>
                        </select>
                    </div>

                    <div className="rich-editor-toolbar-group">
                        {toolbarButton("<b>B</b>", "Bold", () => exec("bold"))}
                        {toolbarButton("<i>I</i>", "Italic", () => exec("italic"))}
                        {toolbarButton("<u>U</u>", "Underline", () => exec("underline"))}
                        {toolbarButton("<s>S</s>", "Strikethrough", () => exec("strikeThrough"))}
                        {toolbarButton("x²", "Superscript", () => exec("superscript"))}
                        {toolbarButton("x₂", "Subscript", () => exec("subscript"))}
                    </div>

                    <div className="rich-editor-toolbar-group">
                        <label className="rich-editor-color" title="Text colour">
                            A
                            <input
                                type="color"
                                onChange={(e) => exec("foreColor", e.target.value)}
                            />
                        </label>

                        <label className="rich-editor-color" title="Highlight colour">
                            🖍
                            <input
                                type="color"
                                onChange={(e) => exec("hiliteColor", e.target.value)}
                            />
                        </label>

                        {toolbarButton("• List", "Bulleted list", () => exec("insertUnorderedList"))}
                        {toolbarButton("1. List", "Numbered list", () => exec("insertOrderedList"))}
                    </div>

                    <div className="rich-editor-toolbar-group">
                        {toolbarButton("≡", "Align left", () => exec("justifyLeft"))}
                        {toolbarButton("≡", "Align center", () => exec("justifyCenter"))}
                        {toolbarButton("≡", "Align right", () => exec("justifyRight"))}
                        {toolbarButton("☷", "Justify", () => exec("justifyFull"))}
                        {toolbarButton("⇥", "Indent", () => exec("indent"))}
                        {toolbarButton("⇤", "Outdent", () => exec("outdent"))}
                    </div>

                    <div className="rich-editor-toolbar-group">
                        {toolbarButton("↶", "Undo", () => exec("undo"))}
                        {toolbarButton("↷", "Redo", () => exec("redo"))}
                        {toolbarButton("Tx", "Remove formatting", () => exec("removeFormat"))}
                        {toolbarButton("—", "Horizontal line", () => exec("insertHorizontalRule"))}
                    </div>

                    <div className="rich-editor-toolbar-group rich-editor-media-group">
                        {toolbarButton("🔗 Link", "Insert link", addLink)}
                        {toolbarButton("🖼 Image", "Upload image", () => handleMediaButton("image"))}
                        {toolbarButton("🎥 Video", "Upload video", () => handleMediaButton("video"))}
                        {toolbarButton("📄 Document", "Attach document", () => handleMediaButton("document"))}
                        {toolbarButton("▦ Table", "Insert table", insertTable)}
                        {toolbarButton("< > HTML", "Insert raw HTML", insertHtml)}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept={
                            mediaType === "image"
                                ? "image/*"
                                : mediaType === "video"
                                    ? "video/*"
                                    : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                        }
                        onChange={handleFileUpload}
                    />
                </div>
            )}

            {sourceMode ? (
                <textarea
                    className="rich-editor-source"
                    value={htmlSource}
                    onChange={handleSourceChange}
                    spellCheck={false}
                    placeholder="Write or paste your HTML here..."
                />
            ) : (
                <div
                    ref={editorRef}
                    className="rich-editor-canvas"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={syncFromEditor}
                    onBlur={syncFromEditor}
                    data-placeholder="Start writing your news article here..."
                />
            )}

            <div className="rich-editor-footer">
                <span>
                    {sourceMode
                        ? "HTML source mode — you can write any valid HTML."
                        : "Tip: select text before using a formatting button. Use HTML Code when you need custom markup."}
                </span>

                <button type="button" className="rich-editor-clear" onClick={clearEditor}>
                    Clear content
                </button>
            </div>
        </div>
    );
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");


const AdminNews = () => {
    const [news, setNews] = useState([]);
    const [categories, setCategories] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [status, setStatus] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);

    const [editingNews, setEditingNews] = useState(null);

    const [form, setForm] = useState({
        title: "",
        excerpt: "",
        content: "",
        news_category_id: "",
        cover_image: null,
        location: "",
        published_at: "",
        status: "draft",
        featured: false,
        active: true,
    });

    const [categoryForm, setCategoryForm] = useState({
        name: "",
        active: true,
    });

    /*
    |--------------------------------------------------------------------------
    | Fetch Categories
    |--------------------------------------------------------------------------
    */

    const fetchCategories = async () => {
        try {
            const response = await api.get("/admin/news/categories");

            const result = await response.data;

            if (result.success) {
                setCategories(result.data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Fetch News
    |--------------------------------------------------------------------------
    */

    const fetchNews = async (requestedPage = page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            params.set("page", requestedPage);
            params.set("per_page", perPage);

            if (search.trim()) {
                params.set("search", search.trim());
            }

            if (categoryId) {
                params.set("category_id", categoryId);
            }

            if (status) {
                params.set("status", status);
            }

            const response = await api.get(`/admin/news?${params.toString()}`);

            const result = await response.data;

            if (result.success) {
                setNews(result.data.data);
                setPagination(result.data);
            }
        } catch (error) {
            console.error("Error fetching news:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setPage(1);
        fetchNews(1);
    }, [search, categoryId, status, perPage]);

    /*
    |--------------------------------------------------------------------------
    | Form
    |--------------------------------------------------------------------------
    */

    const resetForm = () => {
        setForm({
            title: "",
            excerpt: "",
            content: "",
            news_category_id: "",
            cover_image: null,
            location: "",
            published_at: "",
            status: "draft",
            featured: false,
            active: true,
        });

        setEditingNews(null);
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingNews(item);

        setForm({
            title: item.title || "",
            excerpt: item.excerpt || "",
            content: item.content || "",
            news_category_id: item.news_category_id || "",
            cover_image: null,
            location: item.location || "",
            published_at: item.published_at
                ? item.published_at.substring(0, 10)
                : "",
            status: item.status || "draft",
            featured: !!item.featured,
            active: !!item.active,
        });

        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        setForm((previous) => ({
            ...previous,
            [name]:
                type === "checkbox"
                    ? checked
                    : type === "file"
                        ? files[0]
                        : value,
        }));
    };

    /*
    |--------------------------------------------------------------------------
    | Save News
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);

            const formData = new FormData();

            formData.append("title", form.title);
            formData.append("excerpt", form.excerpt);
            formData.append("content", form.content);
            formData.append(
                "news_category_id",
                form.news_category_id || ""
            );
            formData.append("location", form.location);
            formData.append("published_at", form.published_at || "");
            formData.append("status", form.status);
            formData.append("featured", form.featured ? "1" : "0");
            formData.append("active", form.active ? "1" : "0");

            if (form.cover_image) {
                formData.append("cover_image", form.cover_image);
            }

            let url = "/admin/news";

            let response  ; 

            if (editingNews) {
                url = `/admin/news/${editingNews.id}`;

                 /* formData.append("_method", "PUT"); */

                   response = await api.post(url, formData ,  {
                       headers: {
                           "Content-Type": "multipart/form-data"
                       }
                   });

                // Laravel method spoofing for multipart PUT
               
            }else{

                 response = await api.post(url, formData ,  {
                       headers: {
                          
                           "Content-Type": "multipart/form-data"
                       }
                   });
            }


          


            const result = await response.data;

            if (result.success === false) {
                console.error(result);
                alert(
                    result.error ||
                    "Unable to save the news article."
                );
                return;
            }

            if (result.success) {
                showAlert('success', 'success!', "successfully.");
                setShowModal(false);
                resetForm();
                fetchNews(page);
            }
        } catch (error) {
            console.error("Error saving news:", error);
            showAlert('error', 'Error!', "Something went wrong while saving the article.");
        } finally {
            setSaving(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Delete
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (item) => {
        const confirmed = window.confirm(
            `Delete "${item.title}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await api.delete( `/admin/news/${item.id}`);

        

            const result = await response.data;

            if (result.success) {

                showAlert('success', 'success!', "Article deleted successfully.");
                fetchNews(
                    news.length === 1 && page > 1
                        ? page - 1
                        : page
                );
            } else {
                showAlert('error', 'Error!', result.message || "Unable to delete article.");
            }
        } catch (error) {
            console.error(error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Toggle Featured
    |--------------------------------------------------------------------------
    */

    const toggleFeatured = async (item) => {
        try {
            const response  =  await  api.patch( `/admin/news/${item.id}/featured`);
            
            const result = await response.data;

            if (result.success) {
                fetchNews(page);
            }
        } catch (error) {
            console.error(error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Toggle Active
    |--------------------------------------------------------------------------
    */

    const toggleActive = async (item) => {
        try {

            const  response  =  await api.patch(`/admin/news/${item.id}/active`);
       

            const result = await response.data;

            if (result.success) {
                fetchNews(page);
            }
        } catch (error) {
            console.error(error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Category
    |--------------------------------------------------------------------------
    */

    const openCategoryModal = () => {
        setCategoryForm({
            name: "",
            active: true,
        });

        setShowCategoryModal(true);
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();

        try {

            const response  = await  api.post( "/admin/news/categories"  ,  JSON.stringify(categoryForm) );
      

            const result = await response.data;

             if (response.success === false) {
                alert(
                    result.message ||
                    "Unable to create category."
                );
                return;
            } 

            if (result.success) {
                setShowCategoryModal(false);
                fetchCategories();
            }
        } catch (error) {
            console.error(error);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Image URL
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (path) => {
        if (!path) return null;

        if (path.startsWith("http")) {
            return path;
        }

        return `/storage/${path}`;
    };

    return (
        <div className="admin-page">

            {/* HEADER */}

            <div className="admin-page-header">
                <div>
                    <div className="admin-eyebrow">
                        CONTENT MANAGEMENT
                    </div>

                    <h1>News & Updates</h1>

                    <p>
                        Manage legislative updates, constituency
                        activities, announcements and public service
                        news.
                    </p>
                </div>

                <div className="admin-page-header-actions">
                    <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={openCategoryModal}
                    >
                        + Category
                    </button>

                    <button
                        type="button"
                        className="admin-btn admin-btn-primary"
                        onClick={openCreateModal}
                    >
                        + New Article
                    </button>
                </div>
            </div>

            {/* FILTERS */}

            <div className="admin-card news-filter-card">

                <div className="news-filter-search">
                    <label>Search</label>

                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={search}
                        onChange={(e) =>
                            setSearch(e.target.value)
                        }
                    />
                </div>

                <div>
                    <label>Category</label>

                    <select
                        value={categoryId}
                        onChange={(e) =>
                            setCategoryId(e.target.value)
                        }
                    >
                        <option value="">
                            All Categories
                        </option>

                        {categories.map((category) => (
                            <option
                                key={category.id}
                                value={category.id}
                            >
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label>Status</label>

                    <select
                        value={status}
                        onChange={(e) =>
                            setStatus(e.target.value)
                        }
                    >
                        <option value="">All Status</option>
                        <option value="published">
                            Published
                        </option>
                        <option value="draft">
                            Draft
                        </option>
                    </select>
                </div>

                <div>
                    <label>Per Page</label>

                    <select
                        value={perPage}
                        onChange={(e) =>
                            setPerPage(Number(e.target.value))
                        }
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                </div>
            </div>

            {/* TABLE */}

            <div className="admin-card">

                <div className="admin-card-header">
                    <div>
                        <h2>Articles</h2>

                        <span>
                            {pagination?.total || 0} total articles
                        </span>
                    </div>
                </div>

                <div className="admin-table-wrapper">

                    {loading ? (
                        <div className="admin-loading">
                            Loading articles...
                        </div>
                    ) : news.length === 0 ? (
                        <div className="admin-empty">
                            <div className="admin-empty-icon">
                                ◌
                            </div>

                            <h3>No articles found</h3>

                            <p>
                                Create your first news article to
                                get started.
                            </p>

                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={openCreateModal}
                            >
                                Create Article
                            </button>
                        </div>
                    ) : (
                        <table className="admin-table">

                            <thead>
                                <tr>
                                    <th>Article</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                    <th>Published</th>
                                    <th>Featured</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {news.map((item) => (
                                    <tr key={item.id}>

                                        <td>
                                            <div className="news-table-article">

                                                <div className="news-table-image">

                                                    {item.cover_image ? (
                                                        <img
                                                            src={getImageUrl(
                                                                BACKEND_URL + "/storage/" + item.cover_image
                                                            )}
                                                            alt={
                                                                item.title
                                                            }
                                                        />
                                                    ) : (
                                                        <span>
                                                            NEWS
                                                        </span>
                                                    )}

                                                </div>

                                                <div>
                                                    <strong>
                                                        {item.title}
                                                    </strong>

                                                    {item.excerpt && (
                                                        <p>
                                                            {item.excerpt}
                                                        </p>
                                                    )}
                                                </div>

                                            </div>
                                        </td>

                                        <td>
                                            <span className="admin-category-badge">
                                                {item.category?.name ||
                                                    "Uncategorized"}
                                            </span>
                                        </td>

                                        <td>

                                            <button
                                                type="button"
                                                className={`admin-status-badge ${item.active &&
                                                        item.status ===
                                                        "published"
                                                        ? "published"
                                                        : "draft"
                                                    }`}
                                                onClick={() =>
                                                    toggleActive(
                                                        item
                                                    )
                                                }
                                            >
                                                {item.status ===
                                                    "published"
                                                    ? "Published"
                                                    : "Draft"}
                                            </button>

                                        </td>

                                        <td>
                                            {item.published_at
                                                ? new Date(
                                                    item.published_at
                                                ).toLocaleDateString()
                                                : "—"}
                                        </td>

                                        <td>

                                            <button
                                                type="button"
                                                className={`news-featured-toggle ${item.featured
                                                        ? "active"
                                                        : ""
                                                    }`}
                                                onClick={() =>
                                                    toggleFeatured(
                                                        item
                                                    )
                                                }
                                                title={
                                                    item.featured
                                                        ? "Remove featured"
                                                        : "Make featured"
                                                }
                                            >
                                                ★
                                            </button>

                                        </td>

                                        <td>

                                            <div className="admin-table-actions">

                                                <button
                                                    type="button"
                                                    className="admin-action-btn"
                                                    onClick={() =>
                                                        openEditModal(
                                                            item
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    className="admin-action-btn danger"
                                                    onClick={() =>
                                                        handleDelete(
                                                            item
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </td>

                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    )}

                </div>

                {/* PAGINATION */}

                {pagination && (
                    <AdminPagination
                        pagination={pagination}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            fetchNews(newPage);
                        }}
                    />
                )}

            </div>

            {/* ARTICLE MODAL */}

            {showModal && (
                <div
                    className="admin-modal-overlay"
                    onMouseDown={(e) => {
                        if (
                            e.target ===
                            e.currentTarget
                        ) {
                            setShowModal(false);
                        }
                    }}
                >
                    <div className="admin-modal admin-modal-large">

                        <div className="admin-modal-header">

                            <div>
                                <div className="admin-eyebrow">
                                    {editingNews
                                        ? "EDIT ARTICLE"
                                        : "NEW ARTICLE"}
                                </div>

                                <h2>
                                    {editingNews
                                        ? "Edit News Article"
                                        : "Create News Article"}
                                </h2>
                            </div>

                            <button
                                type="button"
                                className="admin-modal-close"
                                onClick={() =>
                                    setShowModal(false)
                                }
                            >
                                ×
                            </button>

                        </div>

                        <form
                            onSubmit={handleSubmit}
                            className="admin-form"
                        >

                            <div className="admin-form-grid">

                                <div className="admin-form-group full">
                                    <label>
                                        Title *
                                    </label>

                                    <input
                                        type="text"
                                        name="title"
                                        value={form.title}
                                        onChange={
                                            handleFormChange
                                        }
                                        required
                                        placeholder="Enter article title"
                                    />
                                </div>

                                <div className="admin-form-group">
                                    <label>
                                        Category
                                    </label>

                                    <select
                                        name="news_category_id"
                                        value={
                                            form.news_category_id
                                        }
                                        onChange={
                                            handleFormChange
                                        }
                                    >
                                        <option value="">
                                            Select category
                                        </option>

                                        {categories.map(
                                            (category) => (
                                                <option
                                                    key={
                                                        category.id
                                                    }
                                                    value={
                                                        category.id
                                                    }
                                                >
                                                    {
                                                        category.name
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>

                                <div className="admin-form-group">
                                    <label>
                                        Location
                                    </label>

                                    <input
                                        type="text"
                                        name="location"
                                        value={
                                            form.location
                                        }
                                        onChange={
                                            handleFormChange
                                        }
                                        placeholder="e.g. Ado-Ekiti"
                                    />
                                </div>

                                <div className="admin-form-group">
                                    <label>
                                        Publication Date
                                    </label>

                                    <input
                                        type="date"
                                        name="published_at"
                                        value={
                                            form.published_at
                                        }
                                        onChange={
                                            handleFormChange
                                        }
                                    />
                                </div>

                                <div className="admin-form-group">
                                    <label>
                                        Status
                                    </label>

                                    <select
                                        name="status"
                                        value={
                                            form.status
                                        }
                                        onChange={
                                            handleFormChange
                                        }
                                    >
                                        <option value="draft">
                                            Draft
                                        </option>

                                        <option value="published">
                                            Published
                                        </option>
                                    </select>
                                </div>

                                <div className="admin-form-group full">
                                    <label>
                                        Excerpt
                                    </label>

                                    <textarea
                                        name="excerpt"
                                        rows="3"
                                        value={
                                            form.excerpt
                                        }
                                        onChange={
                                            handleFormChange
                                        }
                                        placeholder="Short description of the article..."
                                    />
                                </div>

                                <div className="admin-form-group full">
                                    <label>
                                        Content
                                    </label>

                                    <RichNewsEditor
                                        value={form.content}
                                        onChange={(content) =>
                                            setForm((previous) => ({
                                                ...previous,
                                                content,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="admin-form-group full">
                                    <label>
                                        Cover Image
                                    </label>

                                    <input
                                        type="file"
                                        name="cover_image"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={
                                            handleFormChange
                                        }
                                    />

                                    {editingNews?.cover_image && (
                                        <div className="news-existing-image">
                                            <img
                                                src={getImageUrl(
                                                    BACKEND_URL + "/storage/" + editingNews.cover_image
                                                )}
                                                alt=""
                                            />

                                            <span>
                                                Current cover image
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="admin-form-options full">

                                    <label className="admin-checkbox">

                                        <input
                                            type="checkbox"
                                            name="featured"
                                            checked={
                                                form.featured
                                            }
                                            onChange={
                                                handleFormChange
                                            }
                                        />

                                        <span>
                                            Featured article
                                        </span>

                                    </label>

                                    <label className="admin-checkbox">

                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={
                                                form.active
                                            }
                                            onChange={
                                                handleFormChange
                                            }
                                        />

                                        <span>
                                            Active
                                        </span>

                                    </label>

                                </div>

                            </div>

                            <div className="admin-modal-footer">

                                <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() =>
                                        setShowModal(false)
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="admin-btn admin-btn-primary"
                                    disabled={saving}
                                >
                                    {saving
                                        ? "Saving..."
                                        : editingNews
                                            ? "Update Article"
                                            : "Publish Article"}
                                </button>

                            </div>

                        </form>

                    </div>
                </div>
            )}

            {/* CATEGORY MODAL */}

            {showCategoryModal && (
                <div
                    className="admin-modal-overlay"
                    onMouseDown={(e) => {
                        if (
                            e.target ===
                            e.currentTarget
                        ) {
                            setShowCategoryModal(false);
                        }
                    }}
                >
                    <div className="admin-modal">

                        <div className="admin-modal-header">

                            <div>
                                <div className="admin-eyebrow">
                                    NEWS
                                </div>

                                <h2>
                                    New Category
                                </h2>
                            </div>

                            <button
                                type="button"
                                className="admin-modal-close"
                                onClick={() =>
                                    setShowCategoryModal(
                                        false
                                    )
                                }
                            >
                                ×
                            </button>

                        </div>

                        <form
                            onSubmit={
                                handleCategorySubmit
                            }
                            className="admin-form"
                        >

                            <div className="admin-form-group">
                                <label>
                                    Category Name *
                                </label>

                                <input
                                    type="text"
                                    value={
                                        categoryForm.name
                                    }
                                    onChange={(e) =>
                                        setCategoryForm(
                                            (previous) => ({
                                                ...previous,
                                                name:
                                                    e.target
                                                        .value,
                                            })
                                        )
                                    }
                                    required
                                    placeholder="e.g. Legislative Updates"
                                />
                            </div>

                            <label className="admin-checkbox">

                                <input
                                    type="checkbox"
                                    checked={
                                        categoryForm.active
                                    }
                                    onChange={(e) =>
                                        setCategoryForm(
                                            (previous) => ({
                                                ...previous,
                                                active:
                                                    e.target
                                                        .checked,
                                            })
                                        )
                                    }
                                />

                                <span>Active</span>

                            </label>

                            <div className="admin-modal-footer">

                                <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() =>
                                        setShowCategoryModal(
                                            false
                                        )
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="admin-btn admin-btn-primary"
                                >
                                    Create Category
                                </button>

                            </div>

                        </form>

                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminNews;